import { makeStyles } from '@material-ui/styles'
import React from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@material-ui/core'
import DraggablePaper from 'civic-champs-shared/core/confirm-dialog/DraggablePaper'
import { Close as CloseIcon } from '@material-ui/icons'
import { ContainedButton, OutlinedButton } from 'civic-champs-shared'
import cn from 'classnames'

export const useStyles = makeStyles({
  title: {
    cursor: 'move',
    padding: '0 0 20px',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '32px',
    color: '#000000',
  },
  titleStatic: {
    cursor: 'default',
  },
  dialog: {
    background: '#F8FAFF',
    padding: '20px',
    boxSizing: 'border-box',
    position: 'relative',
    maxWidth: '100%',
    width: '700px',
  },
  dialogCloseButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  dialogCloseIcon: {
    fontSize: '22px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0 0',
    margin: 0,
  },
  next: {
    display: 'flex',
    gap: '10px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    // Modal body padding lives on `dialog` (20px). Zero only MUI DialogContent defaults.
    padding: '0 !important',
    overflow: 'visible',
  },
  back: {
    border: 0,
  },
})

interface FormModalProps {
  showing: boolean
  close: () => void
  id: string
  title: string
  className?: string
  /**
   * When false, skips DraggablePaper on the dialog.
   * Do NOT use DraggablePaper when modal content has react-beautiful-dnd list reorder —
   * its transform breaks drag positioning (rows fly off-screen). We've hit this twice:
   * EventDateAndRoleScreen roles, EditGroupApplicationDialog forms. Pass draggable={false}.
   */
  draggable?: boolean
  children: React.ReactNode
}

export const FormModal = ({ showing, close, id, title, className, draggable = true, children }: FormModalProps) => {
  const classes = useStyles()
  return (
    // @ts-ignore
    <Dialog
      open={showing}
      // DraggablePaper + react-beautiful-dnd = broken drag positioning. See draggable prop JSDoc.
      // @ts-ignore
      PaperComponent={draggable ? DraggablePaper : undefined}
      // @ts-ignore
      PaperProps={draggable ? { handle: `#${id}` } : undefined}
      aria-labelledby="draggable-dialog-title"
      maxWidth="xs"
      classes={{ paper: cn(classes.dialog, className) }}
      disableEnforceFocus
    >
      <>
        <DialogTitle
          classes={{ root: cn(classes.title, !draggable && classes.titleStatic) }}
          disableTypography={true}
          id={id}
        >
          {title}
          <IconButton className={classes.dialogCloseButton} onClick={() => close()}>
            <CloseIcon className={classes.dialogCloseIcon} />
          </IconButton>
        </DialogTitle>
        {children}
      </>
    </Dialog>
  )
}

export interface FormModalScreenProps {
  children: React.ReactNode
  onBack?: () => void
  onNext: (...args: any[]) => void
  onDraft?: (...args: any[]) => void
  nextText: string
  backText?: string
  draftText?: string
  backDisabled?: boolean
  nextDisabled?: boolean
  nextIsLoading?: boolean
  disabled?: boolean
  className?: string
  contentClassName?: string
  backClassName?: string
  nextClassName?: string
}

export function FormModalScreen({
  children,
  onBack,
  onNext,
  nextText,
  backText,
  backDisabled,
  nextDisabled,
  nextIsLoading,
  disabled,
  className,
  contentClassName,
  backClassName,
  nextClassName,
  draftText,
  onDraft,
}: FormModalScreenProps) {
  const classes = useStyles()
  return (
    <>
      <DialogContent classes={{ root: cn(classes.content, contentClassName) }}>{children}</DialogContent>
      <DialogActions classes={{ root: cn(classes.actions, className) }}>
        {onBack ? (
          <OutlinedButton
            className={cn(classes.back, backClassName)}
            disabled={disabled || backDisabled}
            onClick={onBack}
          >
            {backText}
          </OutlinedButton>
        ) : (
          <div />
        )}
        <div className={classes.next}>
          {draftText && (
            <OutlinedButton disabled={disabled || nextDisabled} onClick={onDraft}>
              {draftText}
            </OutlinedButton>
          )}
          <ContainedButton className={nextClassName} disabled={disabled || nextDisabled} onClick={onNext}>
            {nextText}
          </ContainedButton>
        </div>
      </DialogActions>
    </>
  )
}
